import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toolsService from "../services/toolsService";
import apiService from "../services/apiService";

/**
 * ImportLMS — Upload .imscc file and extract course content.
 *
 * Flow: Upload -> Parse -> Show multi-select checklist -> User picks items -> Save to InPods
 *
 * Key logic:
 * - Assessment items (quizzes) -> QuestionSet on the module's content lesson (NOT a separate lesson)
 * - Content items (slides, recordings, wiki pages) -> Lesson + Azure Blob upload as DocResource
 * - Questions bulk-created per assessment via /api/questions/bulk/
 */

function ImportLMS() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCourseId, setSavedCourseId] = useState(null);
  const [selectedModules, setSelectedModules] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [includeQuestionBank, setIncludeQuestionBank] = useState(true);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f && (f.name.endsWith('.imscc') || f.name.endsWith('.zip'))) {
      setFile(f);
      setError(null);
    } else {
      setError('Please select a .imscc file (Canvas/Moodle/D2L export)');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.imscc') || f.name.endsWith('.zip'))) {
      setFile(f);
      setError(null);
    } else {
      setError('Please drop a .imscc file');
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setProgress('Uploading file...');
      await toolsService.uploadImscc(file);
      setUploading(false);

      setExtracting(true);
      setProgress('Parsing course structure...');
      const parseResult = await toolsService.parseImscc(file.name);

      setProgress('Extracting assessments...');
      const assessments = await toolsService.extractAssessments(file.name);

      setProgress('Detecting syllabus and COs...');
      const syllabus = await toolsService.extractSyllabus(file.name);

      setProgress('Done!');
      const extractedResults = {
        course: parseResult.course || {},
        modules: parseResult.modules || [],
        totalQuestions: assessments.total_questions || 0,
        syllabusItems: syllabus.total_items || 0,
        assessments,
        syllabus,
        parse: parseResult,
      };
      setResults(extractedResults);

      // Pre-select all modules and items by default
      const modSel = {};
      const itemSel = {};
      (extractedResults.modules || []).forEach((mod, mi) => {
        modSel[mi] = true;
        (mod.items || []).forEach((_, ii) => {
          itemSel[`${mi}-${ii}`] = true;
        });
      });
      setSelectedModules(modSel);
      setSelectedItems(itemSel);

      setExtracting(false);
    } catch (err) {
      setError(`Import failed: ${err.message || 'Unknown error'}`);
      setUploading(false);
      setExtracting(false);
    }
  };

  const toggleModule = (mi) => {
    const newVal = !selectedModules[mi];
    setSelectedModules(prev => ({ ...prev, [mi]: newVal }));
    const mod = results.modules[mi];
    const newItems = { ...selectedItems };
    (mod.items || []).forEach((_, ii) => {
      newItems[`${mi}-${ii}`] = newVal;
    });
    setSelectedItems(newItems);
  };

  const toggleItem = (mi, ii) => {
    const key = `${mi}-${ii}`;
    setSelectedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAll = () => {
    const modSel = {};
    const itemSel = {};
    (results.modules || []).forEach((mod, mi) => {
      modSel[mi] = true;
      (mod.items || []).forEach((_, ii) => {
        itemSel[`${mi}-${ii}`] = true;
      });
    });
    setSelectedModules(modSel);
    setSelectedItems(itemSel);
  };

  const deselectAll = () => {
    setSelectedModules({});
    setSelectedItems({});
  };

  const getSelectedCounts = () => {
    let modules = 0;
    let items = 0;
    (results?.modules || []).forEach((mod, mi) => {
      const modItems = (mod.items || []);
      const hasSelectedItem = modItems.some((_, ii) => selectedItems[`${mi}-${ii}`]);
      if (selectedModules[mi] || hasSelectedItem) modules++;
      modItems.forEach((item, ii) => {
        if (selectedItems[`${mi}-${ii}`] && item.type !== 'assessment') items++;
      });
      if (modItems.length === 0 && selectedModules[mi]) items++;
    });
    return { modules, items };
  };

  const handleSaveToDjango = async () => {
    if (!results) return;
    setSaving(true);
    setProgress('Creating course in InPods...');
    let courseId;
    let lessonCount = 0;
    let outcomeCount = 0;
    let questionCount = 0;
    try {
      // Step 1: Create course (retries with timestamp-appended title on duplicate)
      const baseTitle = results.course.title || file.name.replace('.imscc', '');
      const createdCourse = await apiService.createCourseWithTimestampFallback({
        title: baseTitle,
        description: results.course.description || `Imported from LMS\n\nImported from Canvas IMSCC`,
        department: results.course.department || 'Imported from LMS',
      });
      courseId = createdCourse.id;

      // Step 2: Create lessons from SELECTED non-assessment items only
      const counts = getSelectedCounts();
      let firstLessonId = null;
      const modules = results.modules || [];
      const moduleLessonMap = {}; // maps module title -> first lesson ID in that module
      const allCreatedLessons = []; // [{id, title}] for chapter matching later
      const selectedModuleSet = new Set(); // track which modules user selected

      const pendingUploads = [];

      for (let mi = 0; mi < modules.length; mi++) {
        const mod = modules[mi];
        const items = mod.items || [];
        const modTitle = mod.title || '';

        // Track if any item in this module is selected
        const hasSelected = items.some((_, ii) => selectedItems[`${mi}-${ii}`]) || selectedModules[mi];
        if (hasSelected) selectedModuleSet.add(modTitle);

        if (items.length > 0) {
          for (let ii = 0; ii < items.length; ii++) {
            if (!selectedItems[`${mi}-${ii}`]) continue;
            // SKIP assessment items — they become QuestionSets, not lessons
            if (items[ii].type === 'assessment') continue;

            try {
              setProgress(`Creating lessons... (${lessonCount + 1}/${counts.items})`);
              const itemFiles = items[ii].files || [];

              const newLesson = await apiService.createLesson({
                title: items[ii].title || mod.title || `Module ${mod.position || ''}`,
                course: courseId,
                type: items[ii].type || 'lesson',
                file: '',
                audio_transcript: '',
                summary: '',
              });
              const newLessonId = newLesson.id;
              if (!firstLessonId) firstLessonId = newLessonId;
              if (!moduleLessonMap[modTitle]) moduleLessonMap[modTitle] = newLessonId;
              allCreatedLessons.push({ id: newLessonId, title: items[ii].title || '' });
              lessonCount++;

              // Queue file upload — each file goes to ITS OWN lesson (not shared per module)
              const rawPath = itemFiles.find(f => {
                const fp = typeof f === 'string' ? f : f.href || '';
                return fp.match(/\.(pptx|pdf|docx|ppt|doc)$/i);
              });
              if (rawPath) {
                const resourcePath = typeof rawPath === 'string' ? rawPath : rawPath?.href || '';
                pendingUploads.push({ lessonId: newLessonId, resourcePath, title: items[ii].title });
              }
            } catch (e) {
              console.error('Lesson create failed:', items[ii].title, e.message);
            }
          }
        } else if (selectedModules[mi]) {
          try {
            setProgress(`Creating lessons... (${lessonCount + 1}/${counts.items})`);
            const newLesson = await apiService.createLesson({
              title: mod.title || `Module ${mod.position || ''}`,
              course: courseId,
              type: 'lesson',
              file: '',
              audio_transcript: '',
              summary: '',
            });
            const newLessonId = newLesson.id;
            if (!firstLessonId) firstLessonId = newLessonId;
            if (!moduleLessonMap[modTitle]) moduleLessonMap[modTitle] = newLessonId;
            lessonCount++;
          } catch (e) {
            console.warn('Lesson create failed:', mod.title, e);
          }
        }
      }

      // Step 2b: Create placeholder lessons for modules that have assessments but no content items
      // Without this, assessment-only modules have no lesson to attach quizzes to
      const assessments_list = results.assessments?.assessments || [];
      for (let mi = 0; mi < modules.length; mi++) {
        const mod = modules[mi];
        const modTitle = mod.title || '';
        if (moduleLessonMap[modTitle]) continue; // Already has a lesson
        if (!selectedModuleSet.has(modTitle)) continue; // Not selected

        // Check if this module has any assessments
        const hasAssessments = assessments_list.some(a => a.module === modTitle && (a.questions || []).length > 0);
        if (!hasAssessments) continue;

        try {
          setProgress(`Creating lesson for assessment module: ${modTitle}`);
          const newLesson = await apiService.createLesson({
            title: modTitle,
            course: courseId,
            type: 'lesson',
            file: '',
            audio_transcript: '',
            summary: '',
          });
          const newLessonId = newLesson.id;
          if (!firstLessonId) firstLessonId = newLessonId;
          moduleLessonMap[modTitle] = newLessonId;
          lessonCount++;
        } catch (e) {
          console.warn('Placeholder lesson create failed:', modTitle, e);
        }
      }

      // Step 3: Create outcomes from syllabus
      const syllabusItems = results.syllabus?.items || results.syllabus?.topics || [];
      if (syllabusItems.length > 0) setProgress(`Creating course outcomes...`);
      for (const item of syllabusItems.slice(0, 30)) {
        try {
          await apiService.createOutcome({
            short_name: item.code || `CO${outcomeCount + 1}`,
            description: item.title || item.topic || item.name || 'Imported outcome',
            course: courseId,
          });
          outcomeCount++;
        } catch (e) {
          console.warn('Outcome create failed:', e);
        }
      }

      // Step 4: Create questions — map each assessment to its module's lesson
      if (firstLessonId) {
        const assessments = results.assessments?.assessments || [];
        const allLessonIds = Object.values(moduleLessonMap);
        let roundRobinIdx = 0;

        // Build a chapter-number lookup from actually created lessons
        // e.g. lesson "Chapter 8 - Heart (slides)" -> extract "8" -> lessonId
        const chapterLessonMap = {};
        for (const les of allCreatedLessons) {
          const chMatch = (les.title || '').match(/chapter\s*(\d+)/i);
          if (chMatch) {
            const chNum = chMatch[1].replace(/^0+/, '') || chMatch[1];
            chapterLessonMap[chNum] = les.id;
          }
        }

        // Count questions from selected modules + Question Bank (if toggled on)
        let selectedQuestionTotal = 0;
        for (const a of assessments) {
          const m = a.module || '';
          if (m === 'Question Bank') {
            if (includeQuestionBank) selectedQuestionTotal += (a.questions || []).length;
          } else if (selectedModuleSet.has(m)) {
            selectedQuestionTotal += (a.questions || []).length;
          }
        }

        for (const assessment of assessments) {
          const questions = assessment.questions || [];
          if (questions.length === 0) continue;

          const assessModule = assessment.module || '';

          // SKIP assessments from modules user didn't select
          if (assessModule === 'Question Bank') {
            if (!includeQuestionBank) continue;
          } else if (!selectedModuleSet.has(assessModule)) {
            continue;
          }

          // Find the lesson: (1) module match, (2) chapter number match, (3) round-robin
          let targetLessonId = moduleLessonMap[assessModule];

          if (!targetLessonId) {
            // Try chapter number matching (e.g. "LoefflerCh08" -> chapter 8 -> Ch8 lesson)
            const chMatch = (assessment.title || '').match(/ch(?:apter)?\s*[_\-]?\s*(\d+)/i);
            if (chMatch) {
              const chNum = chMatch[1].replace(/^0+/, '') || chMatch[1];
              if (chapterLessonMap[chNum]) {
                targetLessonId = chapterLessonMap[chNum];
              }
            }
          }

          if (!targetLessonId && allLessonIds.length > 0) {
            targetLessonId = allLessonIds[roundRobinIdx % allLessonIds.length];
            roundRobinIdx++;
          }
          if (!targetLessonId) targetLessonId = firstLessonId;

          const setName = assessment.title || `Imported from ${assessModule}`;
          try {
            setProgress(`Importing questions... (${questionCount}/${selectedQuestionTotal}) - ${setName}`);
            const qs = await apiService.createQuestionSet({
              lesson: targetLessonId,
              name: setName,
              question_count: questions.length,
              answer_style: 'multiple_choice',
            });
            const qsId = qs.id;

            // Bulk create all questions in one request
            const bulkQuestions = questions.map(q => ({
              text: q.text || q.question_text || q.stem || 'Imported question',
              type: 'multiple_choice',
              answer: q.answer || q.correct_answer || '',
              blooms_taxonomy: q.blooms || '',
              difficulty: q.difficulty || 'medium',
            }));

            try {
              const bulkData = await apiService.createQuestionsBulk({
                question_set: qsId,
                questions: bulkQuestions,
              });
              questionCount += bulkData.created || bulkQuestions.length;
            } catch (bulkErr) {
              // Fallback: individual creates if bulk endpoint not available
              console.warn('Bulk create failed, falling back to individual:', bulkErr.message);
              for (const q of bulkQuestions) {
                try {
                  await apiService.createQuestion({ question_set: qsId, ...q });
                  questionCount++;
                } catch (e) { /* skip */ }
              }
            }
          } catch (e) {
            console.warn('Question set create failed:', setName, e);
          }
        }
      }

      // Step 5: Upload files — PPTX/PDF go to BOTH LST (for Lecture Player) and Azure (for doc preview)
      if (pendingUploads.length > 0) {
        setProgress(`Uploading ${pendingUploads.length} files...`);
        let uploaded = 0;
        let lstUploaded = 0;
        for (let i = 0; i < pendingUploads.length; i += 3) {
          const batch = pendingUploads.slice(i, i + 3);
          await Promise.allSettled(batch.map(async ({ lessonId, resourcePath, title }) => {
            const ext = resourcePath.split('.').pop().toLowerCase();
            const cleanTitle = (title || 'file').replace(/[^a-zA-Z0-9_\- ]/g, '');
            const filename = `${cleanTitle}.${ext}`;

            // 5a: Upload raw file to LST for Lecture Player (PPTX/PDF as-is, no conversion)
            try {
              const rawBlob = await apiService.getImsccRawFileBlob({
                filename: file.name,
                resource_path: resourcePath,
              });

              if (rawBlob && rawBlob.size > 1000) {
                const lstForm = new FormData();
                lstForm.append('file', rawBlob, filename);
                await apiService.uploadLstFile(lstForm);
                lstUploaded++;
                console.log('LST upload OK:', filename);
              }
            } catch (e) {
              console.warn('LST upload failed for', title, ':', e.message);
            }

            // 5b: Upload to Azure for document preview (with optional PDF conversion)
            try {
              const fileBlob = await apiService.getImsccRawFileAsPdfBlob({
                filename: file.name,
                resource_path: resourcePath,
              });

              if (!fileBlob) return;

              const urlRes = await apiService.generateUploadUrl(filename, 'lesson_doc', lessonId);
              if (urlRes.success && urlRes.data?.upload_url) {
                const uploadRes = await apiService.uploadToAzureBlob(urlRes.data.upload_url, fileBlob);
                if (uploadRes.success) {
                  await apiService.confirmFileUpload(urlRes.data.blob_name, filename, ext, lessonId, 'lesson', 0, urlRes.data.container_name);
                  uploaded++;
                }
              }
            } catch (e) {
              console.warn('Azure upload failed for', title, ':', e.message);
            }
          }));
          setProgress(`Uploading files... (${Math.min(i + 3, pendingUploads.length)}/${pendingUploads.length})`);
        }
        setProgress(`Saved! ${lessonCount} lessons, ${outcomeCount} outcomes, ${questionCount} questions. ${lstUploaded} files ready for Lecture Player, ${uploaded} uploaded to cloud.`);
      } else {
        setProgress(`Saved! ${lessonCount} lessons, ${outcomeCount} outcomes, ${questionCount} questions.`);
      }

      setSavedCourseId(courseId);
    } catch (err) {
      const partialMsg = courseId ? ` Course was created (ID: ${courseId}) with ${lessonCount} lessons, ${outcomeCount} outcomes, ${questionCount} questions saved before the error.` : '';
      setError(`Save failed: ${err.message}${partialMsg}`);
      if (courseId) setSavedCourseId(courseId);
    } finally {
      setSaving(false);
    }
  };

  const counts = results ? getSelectedCounts() : { modules: 0, items: 0 };

  return (
    <div>
      <div className="course-detail-header">
        <button className="btn btn-secondary" onClick={() => navigate('/courses')}>
          &larr; Back to Courses
        </button>
        <h1 style={{ marginTop: '1rem' }}>Import Course from LMS</h1>
        <p style={{ color: 'var(--text-light)' }}>
          Upload a .imscc file exported from Canvas, Moodle, or D2L Brightspace
        </p>
      </div>

      {/* Info box */}
      <div style={{
        background: '#e8f4fd', borderLeft: '4px solid #4a90e2',
        padding: '12px 16px', borderRadius: '0 6px 6px 0', marginBottom: '20px', fontSize: '14px'
      }}>
        <strong>Supported platforms:</strong> Canvas (full), D2L Brightspace (full), Moodle (CC 1.1, limited), Blackboard
      </div>

      {/* Upload area */}
      {!results && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
          padding: '24px', marginBottom: '20px'
        }}>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
            style={{
              border: '2px dashed #ddd', borderRadius: '12px', padding: '50px',
              textAlign: 'center', background: '#fafafa', cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>📦</div>
            <div style={{ fontSize: '16px', color: '#555', marginBottom: '8px' }}>
              {file ? file.name : 'Drop .imscc file here or click to browse'}
            </div>
            {file && (
              <div style={{ fontSize: '13px', color: '#888' }}>
                {(file.size / (1024 * 1024)).toFixed(1)} MB
              </div>
            )}
            <input ref={fileRef} type="file" accept=".imscc,.zip" onChange={handleFileSelect} style={{ display: 'none' }} />
          </div>

          {error && (
            <div style={{ color: '#dc3545', marginTop: '12px', fontSize: '14px' }}>{error}</div>
          )}

          {file && !uploading && !extracting && (
            <button
              className="btn btn-primary"
              style={{ marginTop: '16px', fontSize: '16px', padding: '12px 32px' }}
              onClick={handleImport}
            >
              Import Course
            </button>
          )}

          {(uploading || extracting) && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', color: '#4a90e2', fontWeight: 600, marginBottom: '8px' }}>
                {progress}
              </div>
              <div style={{ background: '#e0e0e0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                <div style={{
                  width: extracting ? '80%' : '30%',
                  height: '100%', background: '#4a90e2', borderRadius: '4px',
                  transition: 'width 1s'
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results with multi-select */}
      {results && (
        <div>
          <div style={{
            background: '#d4edda', borderLeft: '4px solid #28a745',
            padding: '12px 16px', borderRadius: '0 6px 6px 0', marginBottom: '20px', fontSize: '14px', color: '#155724'
          }}>
            <strong>Import complete!</strong> Course extracted successfully. Deselect any items you don't want to import.
          </div>

          {/* Course info + stats */}
          <div className="card" style={{ padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ marginBottom: '16px' }}>{results.course.title || 'Imported Course'}</h2>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', padding: '16px 24px', background: '#f8f9fa', borderRadius: '8px', minWidth: '100px' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#042F54' }}>{results.modules.length}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Modules</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px 24px', background: counts.items > 0 ? '#d4edda' : '#fff3cd', borderRadius: '8px', minWidth: '100px' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#042F54' }}>{counts.items}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Lessons Selected</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px 24px', background: '#f8f9fa', borderRadius: '8px', minWidth: '100px' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#042F54' }}>{results.totalQuestions}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Questions</div>
              </div>
              <div style={{ textAlign: 'center', padding: '16px 24px', background: '#f8f9fa', borderRadius: '8px', minWidth: '100px' }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#042F54' }}>{results.syllabusItems}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Syllabus Topics</div>
              </div>
            </div>
          </div>

          {/* Module/Item checklist */}
          <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Select Modules & Lessons to Import</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={selectAll}>Select All</button>
                <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '12px' }} onClick={deselectAll}>Deselect All</button>
              </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              {(results.modules || []).map((mod, mi) => {
                const items = mod.items || [];
                const allItemsSelected = items.length > 0
                  ? items.every((_, ii) => selectedItems[`${mi}-${ii}`])
                  : !!selectedModules[mi];
                const someItemsSelected = items.some((_, ii) => selectedItems[`${mi}-${ii}`]);

                return (
                  <div key={mi} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <div
                      onClick={() => toggleModule(mi)}
                      style={{
                        padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        background: allItemsSelected ? '#f0f8ff' : someItemsSelected ? '#fffef0' : '#fff',
                        fontWeight: 600, fontSize: '14px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allItemsSelected}
                        ref={el => { if (el) el.indeterminate = someItemsSelected && !allItemsSelected; }}
                        onChange={() => toggleModule(mi)}
                        style={{ accentColor: '#042F54', width: '16px', height: '16px' }}
                      />
                      <span style={{ color: '#042F54' }}>{mod.title || `Module ${mi + 1}`}</span>
                      <span style={{ fontSize: '12px', color: '#888', fontWeight: 400 }}>
                        ({items.length} item{items.length !== 1 ? 's' : ''})
                      </span>
                    </div>

                    {items.map((item, ii) => (
                      <div
                        key={ii}
                        onClick={() => toggleItem(mi, ii)}
                        style={{
                          padding: '6px 16px 6px 44px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                          fontSize: '13px', color: selectedItems[`${mi}-${ii}`] ? '#333' : '#aaa',
                          background: selectedItems[`${mi}-${ii}`] ? '#fff' : '#fafafa',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedItems[`${mi}-${ii}`]}
                          onChange={() => toggleItem(mi, ii)}
                          style={{ accentColor: '#042F54' }}
                        />
                        <span>{item.title || `Item ${ii + 1}`}</span>
                        {item.type === 'assessment' && (
                          <span style={{ fontSize: '10px', color: '#F56A3F', fontWeight: 600, marginLeft: '4px' }}>QUIZ</span>
                        )}
                        {(item.type === 'slides' || item.type === 'recording') && (
                          <span style={{ fontSize: '10px', color: '#4a90e2', fontWeight: 600, marginLeft: '4px' }}>
                            {item.type === 'slides' ? 'SLIDES' : 'RECORDING'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Question Bank Toggle + Question Preview */}
          {results.totalQuestions > 0 && (
            <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
              {(() => {
                const qbCount = (results.assessments?.assessments || [])
                  .filter(a => a.module === 'Question Bank')
                  .reduce((sum, a) => sum + (a.questions || []).length, 0);
                return qbCount > 0 ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
                    padding: '10px 14px', background: includeQuestionBank ? '#e8f5e9' : '#f5f5f5',
                    borderRadius: '8px', border: '1px solid #e0e0e0'
                  }}>
                    <input
                      type="checkbox"
                      checked={includeQuestionBank}
                      onChange={() => setIncludeQuestionBank(!includeQuestionBank)}
                      style={{ accentColor: '#042F54', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#042F54' }}>
                      Include Question Bank ({qbCount.toLocaleString()} questions)
                    </span>
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      Textbook question banks distributed across selected lessons
                    </span>
                  </div>
                ) : null;
              })()}
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>
                Question Preview ({results.totalQuestions} questions extracted)
              </h3>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                {(() => {
                  const allQs = [];
                  (results.assessments?.assessments || []).forEach(a => {
                    (a.questions || []).forEach(q => allQs.push({ ...q, _module: a.module, _assessment: a.title }));
                  });
                  return allQs.slice(0, 10).map((q, i) => (
                    <div key={i} style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
                      <div>
                        <span style={{ color: '#042F54', fontWeight: 600, marginRight: '8px' }}>Q{i + 1}.</span>
                        <span style={{ color: '#333' }}>{(q.text || q.question_text || q.stem || '').substring(0, 150)}{(q.text || '').length > 150 ? '...' : ''}</span>
                        <span style={{ color: '#888', fontSize: '11px', marginLeft: '8px' }}>({q._module} / {q._assessment})</span>
                      </div>
                      {q.choices && q.choices.length > 0 && (
                        <div style={{ marginTop: '4px', paddingLeft: '28px', fontSize: '12px', color: '#666' }}>
                          {q.choices.slice(0, 4).map((c, ci) => (
                            <span key={ci} style={{ marginRight: '12px', color: c === q.correct ? '#28a745' : '#666', fontWeight: c === q.correct ? 600 : 400 }}>
                              {String.fromCharCode(65 + ci)}) {typeof c === 'string' ? c.substring(0, 40) : c?.text?.substring(0, 40) || ''}
                              {c === q.correct ? ' ✓' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ));
                })()}
                {results.totalQuestions > 10 && (
                  <div style={{ padding: '8px 16px', fontSize: '12px', color: '#888', textAlign: 'center' }}>
                    ... and {results.totalQuestions - 10} more questions
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {!savedCourseId ? (
              <button
                className="btn btn-primary"
                style={{ fontSize: '16px', padding: '12px 24px' }}
                onClick={handleSaveToDjango}
                disabled={saving || counts.items === 0}
              >
                {saving ? 'Saving...' : `Save ${counts.items} Lessons to InPods`}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                style={{ fontSize: '16px', padding: '12px 24px' }}
                onClick={() => navigate(`/course/${savedCourseId}`)}
              >
                Go to Course
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => { setResults(null); setFile(null); setSavedCourseId(null); }}>
              Import Another
            </button>
            {saving && (
              <span style={{ fontSize: '14px', color: '#4a90e2', fontWeight: 600 }}>{progress}</span>
            )}
            {savedCourseId && (
              <span style={{ fontSize: '14px', color: '#28a745', fontWeight: 600 }}>{progress}</span>
            )}
          </div>

          {error && (
            <div style={{ color: '#dc3545', marginTop: '12px', fontSize: '14px' }}>{error}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default ImportLMS;
